import React, { ReactElement, ReactNode, useEffect, useRef, useState } from 'react'
import TextInputDropDown from '../TextInputDropDown'
import PopperContent from '../PopperContent'
import { ReactState } from '../../Types/Types'

export type AutoCompleteOption = {
    label: string
    value: string
}

export type AutoCompleteProps = {
    classNames?: string
    textInputValue: string
    setTextInputValue: (value: string) => void
    selectList: string[] | AutoCompleteOption[]
    noListItemsMessage: string
    noListItemsIncludeValue?: boolean
    textInputLabel?: string
    isOpen?: boolean
    setIsOpen?: (value: boolean) => void
    autoCompleteCallback?: () => void
    placeholder?: string
    showAllOptionsOnReopen?: boolean
}

/**
 *  Generic autocomplete text field
 *
 */
const AutoComplete: React.FC<AutoCompleteProps> = ({
    classNames,
    textInputLabel,
    textInputValue,
    setTextInputValue,
    selectList,
    noListItemsMessage,
    noListItemsIncludeValue,
    isOpen,
    setIsOpen,
    autoCompleteCallback,
    placeholder,
    showAllOptionsOnReopen
}: AutoCompleteProps): ReactElement => {
    const [filteredList, setFilteredList] = useState<string[] | AutoCompleteOption[]>([])

    const isComplexOption: boolean = selectList.length > 0 && typeof selectList[0] !== 'string'

    const [isOpenInternal, setIsOpenInternal]: ReactState<boolean> = useState<boolean>(isOpen ?? false)

    useEffect(() => {
        if (isOpen !== undefined) {
            setIsOpenInternal(isOpen)
        }
    }, [isOpen])

    /**
     * A method that retrieves the folder items as a list of react nodes.
     * @param list The list of folder names as a string array.
     */
    const getListItemsAsReactNodes = (list: string[]): ReactNode => {
        const listItems = list?.map(
            (item): ReactElement => (
                <li
                    key={item}
                    className='autoCompleteListItem'
                    onClick={(): void => {
                        if (isComplexOption) {
                            setTextInputValue(
                                (filteredList as AutoCompleteOption[]).find((entry) => entry.label === item)?.value ??
                                    ''
                            )
                        } else {
                            setTextInputValue(item)
                        }

                        setIsOpen ? setIsOpen(false) : setIsOpenInternal(false)
                    }}
                >
                    {item}
                </li>
            )
        )

        return listItems
    }

    /**
     * A method that returns the correct folder list as react nodes.
     */
    const getListOptions = (): ReactNode => {
        let list: string[] = []

        if (selectList && selectList.length > 0) {
            if (!isComplexOption) {
                if (textInputValue.length < 1) {
                    list = selectList as string[]
                } else {
                    list = filteredList as string[]
                }
            } else if (textInputValue.length < 1) {
                list = (selectList as AutoCompleteOption[]).map((entry) => entry.label)
            } else {
                list = (filteredList as AutoCompleteOption[]).map((entry) => entry.label)
            }
        }

        return getListItemsAsReactNodes(list)
    }

    /**
     * The method that updates the list of reports as the user types inside the text field.
     * @param value THe value inside the text input field.
     */
    const updateSelectList = (value: string): void => {
        if (!isComplexOption) {
            setFilteredList(
                (selectList as string[]).filter((entry: string) => entry.toLowerCase().includes(value.toLowerCase()))
            )
        } else {
            setFilteredList(
                (selectList as AutoCompleteOption[]).filter((entry: AutoCompleteOption) =>
                    entry.label.toLowerCase().includes(value.toLowerCase())
                )
            )
        }
    }

    /**
     * An event function that is called on the updating of the text field value.
     * @param value The new value typed inside the text field.
     */
    const onTextFieldChange = (value: string): void => {
        setTextInputValue(value)

        // If dropdown isn't open then open it if a value is changed.
        if (!isOpen) {
            setIsOpen ? setIsOpen(true) : setIsOpenInternal(true)
        }

        updateSelectList(value)
    }

    const textElemRef = useRef(null)

    return (
        <React.Fragment>
            <div className='autoComplete'>
                <TextInputDropDown
                    labelInputClassName={classNames}
                    value={textInputValue}
                    labelLeft={textInputLabel}
                    onChange={(value): void => {
                        onTextFieldChange(value)
                    }}
                    elementRef={textElemRef}
                    onClick={(): void => {
                        // Update list on click.
                        if (showAllOptionsOnReopen) {
                            updateSelectList('')
                        } else {
                            updateSelectList(textInputValue)
                        }
                        // Handle visibiliy of popper.
                        if (autoCompleteCallback) {
                            autoCompleteCallback()
                        } else {
                            setIsOpen ? setIsOpen(true) : setIsOpenInternal(true)
                        }
                    }}
                    customDropdown={true}
                    isOpen={isOpen}
                    placeholder={placeholder}
                />
            </div>
            <PopperContent
                anchorEl={textElemRef.current}
                open={isOpenInternal}
                setOpen={setIsOpenInternal}
                isClickAwayHandled={true}
                clickAwayCallBack={autoCompleteCallback}
            >
                <div
                    style={{
                        width: '100%',
                        maxWidth: '400px'
                    }}
                >
                    {filteredList.length > 0 || textInputValue.length === 0 ? (
                        <ul className='autoCompleteItemList'>{getListOptions()}</ul>
                    ) : (
                        <span className='noListItems'>
                            {!noListItemsIncludeValue
                                ? noListItemsMessage
                                : `${textInputValue} - ${noListItemsMessage}`}
                        </span>
                    )}
                </div>
            </PopperContent>
        </React.Fragment>
    )
}
export default AutoComplete
